/**
 * Feature flags for gradual rollout of new features
 * 
 * Usage:
 * import { FEATURE_FLAGS } from '@/utils/config/featureFlags';
 * if (FEATURE_FLAGS.SOME_FEATURE) { ... }
 * 
 * Note: NEW_NAVIGATION flag was removed on 2025-11-28 after consolidating
 * to a single navigation structure. Old navigation.tsx was deleted.
 */

// REQUIREMENTS SUMMARY:
// - Added ENABLE_BRAND_THEME_SWITCHER feature flag to control visibility of theme switching functionality.
// - Added ENABLE_EMAIL_TEMPLATE_EDITING feature flag to control gym template customization.
// - Added ENABLE_HELP_AND_SUPPORT feature flag to control visibility of help and support link in user dropdown.
// - Default state is false, as the chatbot is now the primary support channel. "My Support Requests" link is always visible regardless of this flag.
// - Added ENABLE_FAMILY_GUARDIAN_ACCESS (U1) to gate the multi-guardian Family/Guardian Access feature. Default false; everything stays hidden/inert while off.

export const FEATURE_FLAGS = {
  /**
   * Enforce communication preferences when sending notifications
   * 
   * When enabled: Notifications will only be sent via channels the user has enabled
   * in their communication preferences (email, sms, app_push, etc.)
   * 
   * When disabled: All notifications are sent regardless of user preferences
   * (default behavior for CheerVue)
   * 
   * Default: false (disabled)
   * Enable for testing: Set NEXT_PUBLIC_ENFORCE_COMMUNICATION_PREFERENCES=true
   */
  ENFORCE_COMMUNICATION_PREFERENCES: process.env.NEXT_PUBLIC_ENFORCE_COMMUNICATION_PREFERENCES === 'true',

  /**
   * Enable event-based targeting in Announcement Center message composer
   * 
   * When enabled: Shows "Events" option in the scope selector, allowing
   * staff to target messages to attendees of specific events.
   * 
   * Default: false (hidden - feature not fully implemented)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_EVENT_SCOPE=true
   */
  ENABLE_EVENT_SCOPE: process.env.NEXT_PUBLIC_ENABLE_EVENT_SCOPE === 'true',

  /**
   * Mint the program-enrollment invoice on payment success instead of at
   * checkout start (#2725, plan 2026-08-07-002).
   *
   * When enabled: /api/public/programs/enroll returns a PaymentOrder (the
   * tryout/private-lesson/package pattern) and creates NO athlete_invoices
   * row until the payment succeeds; reservations ride on the
   * pending_enrollment_checkouts anchor. When disabled: the legacy pending
   * invoice branch runs unchanged.
   *
   * Default: false (ship dark; flip in Vercel env after the sandbox
   * verification gate passes). The payments route accepts BOTH shapes for
   * the flag's lifetime, so flipping needs no deploy.
   */
  ENABLE_ENROLLMENT_MINT_ON_PAYMENT: process.env.NEXT_PUBLIC_ENABLE_ENROLLMENT_MINT_ON_PAYMENT === 'true',

  /**
   * Enable individual user targeting in Announcement Center message composer
   *
   * When enabled: Shows "Specific Person" option in the scope selector, allowing
   * sending staff to message individual people by searching. Assignment-scoped
   * senders (coaches/instructors) are server-limited to people on their rosters.
   *
   * Default: TRUE (coach comms unification, resolved product decision
   * 2026-07-17 — individual messaging ships for all sending staff). The env var
   * is now a KILL SWITCH: the deploy itself carries the flip, and setting
   * NEXT_PUBLIC_ENABLE_INDIVIDUAL_SCOPE=false hides it again without a code change.
   */
  ENABLE_INDIVIDUAL_SCOPE: process.env.NEXT_PUBLIC_ENABLE_INDIVIDUAL_SCOPE !== 'false',

  /**
   * Require AutoPay for monthly classes (per-class toggle).
   *
   * When enabled: gym staff can turn on "Require AutoPay for monthly classes" on a
   * monthly class, and (later phases) the enrollment checkout requires a saved
   * payment method + autopay consent. When disabled: the toggle is hidden and
   * enrollment is unchanged.
   *
   * Mirrors the standing-lesson AutoPay requirement for monthly group classes.
   * Default: false (hidden — feature shipping in phases behind this flag).
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_REQUIRE_MONTHLY_CLASS_AUTOPAY=true
   */
  ENABLE_REQUIRE_MONTHLY_CLASS_AUTOPAY:
    process.env.NEXT_PUBLIC_ENABLE_REQUIRE_MONTHLY_CLASS_AUTOPAY === 'true',

  // 2026-04-26: Removed 7 ENABLE_CAMP_* flags (DAILY_RATES, GROUP_DISCOUNTS, THEMES,
  // SPECIAL_ACTIVITIES, MATERIALS_INCLUDED, PACKAGES_FEATURES, SERVICES) and the
  // ENABLE_CAMPS_EVENTS feature gate. Camps, clinics, workshops, and open gyms are
  // now created as session-based Classes (see featureGuide entry `clinics_camps_workshops`).

  /**
   * Enable direct creation of parent accounts by gym staff
   * 
   * When enabled: Shows "Add Parent" button on parents list page.
   * When disabled: Gyms must tell parents to register via the public sign-up page.
   * 
   * Default: false (disabled)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_PARENT_CREATION=true
   */
  ENABLE_PARENT_CREATION: process.env.NEXT_PUBLIC_ENABLE_PARENT_CREATION === 'true',

  /**
   * Enable parent invitations by gym staff
   * 
   * When enabled: Shows "Invite Parent" button on parents list page.
   * When disabled: Gyms must tell parents to register via the public sign-up page.
   * 
   * Default: false (disabled)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_PARENT_INVITATION=true
   */
  ENABLE_PARENT_INVITATION: process.env.NEXT_PUBLIC_ENABLE_PARENT_INVITATION === 'true',

  /**
   * Enable editing of parent account details by gym staff
   * 
   * When enabled: Shows "Edit" and "Reset Password" buttons for parent accounts.
   * When disabled: Parent accounts are managed by the platform, not the gym.
   * 
   * Default: false (disabled)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_PARENT_EDITING=true
   */
  ENABLE_PARENT_EDITING: process.env.NEXT_PUBLIC_ENABLE_PARENT_EDITING === 'true',

  /**
   * Enable Public Profile feature for gym catalog
   * 
   * When enabled: Shows "Public Profile" tab in Organization Settings where gyms can:
   * - Toggle catalog visibility (show/hide gym in public directory)
   * - Add gym photo for catalog listing
   * - Write description for public catalog
   * - Preview how their gym appears to parents/athletes
   * 
   * When disabled: Public Profile tab is hidden. This prevents showing a sparse
   * public directory when the platform has few gyms (looks unestablished).
   * 
   * Recommendation: Enable when platform has 100+ gym customers to ensure
   * a robust, established-looking public catalog.
   * 
   * Default: false (disabled)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_PUBLIC_PROFILE=true
   */
  ENABLE_PUBLIC_PROFILE: true, // Always enabled - controls gym-specific public presence (catalog, photo, etc.)
  
  /**
   * Enable Gym Discovery feature (global directory)
   * 
   * When enabled: Shows the global directory of all gyms at /gyms.
   * When disabled: The global directory is hidden, but gym-specific catalogs remain active.
   * 
   * Default: false (disabled)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_GYM_DISCOVERY=true
   */
  ENABLE_GYM_DISCOVERY: process.env.NEXT_PUBLIC_ENABLE_GYM_DISCOVERY === 'true',

  /**
   * Enable Brand Theme Switcher in Admin Settings
   * 
   * When enabled: Shows the Brand Theme switcher in the Admin Dashboard,
   * allowing admins to choose between different color schemes.
   * 
   * When disabled: The Brand Theme switcher is hidden from the UI.
   * 
   * Default: false (disabled)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_BRAND_THEME_SWITCHER=true
   */
  ENABLE_BRAND_THEME_SWITCHER: process.env.NEXT_PUBLIC_ENABLE_BRAND_THEME_SWITCHER === 'true',

  /**
   * Enable Email Template Editing for gyms
   * 
   * When enabled: Shows "Email Templates" tab in Email Configuration where gyms can
   * customize the content of automated system emails.
   * 
   * When disabled: The tab is hidden and gyms use platform-wide professional defaults.
   * 
   * Default: false (hidden for launch)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_EMAIL_TEMPLATE_EDITING=true
   *
   * ROLLOUT (#1616): the assistant KB self-switches on this flag — the
   * `email_template_editor` and `private_lesson_cancellation_email` entries in
   * lib/anthropic/knowledge/featureGuide.ts and the matching guidance in
   * lib/anthropic/prompts/chatAssistant.ts branch on it at module load, so the
   * env flip (which redeploys) updates what the assistant says with no code
   * change. Guardrail tests cover BOTH flag states. The remaining flip-day
   * steps (owner-gated prod migrations, the Vercel env change + redeploy, the
   * post-flip prod smoke, publishing the changelog post, rollback) live in the
   * runbook: docs/plans/2026-07-09-email-template-editor-flag-flip-runbook.md.
   */
  ENABLE_EMAIL_TEMPLATE_EDITING: process.env.NEXT_PUBLIC_ENABLE_EMAIL_TEMPLATE_EDITING === 'true',

  /**
   * Enable SMS OTP (One-Time Password) for authentication
   * 
   * When enabled: Users can choose to receive a verification code via SMS.
   * When disabled: Only Email OTP is available.
   * 
   * Default: false (disabled to save costs)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_SMS_OTP=true
   */
  ENABLE_SMS_OTP: process.env.NEXT_PUBLIC_ENABLE_SMS_OTP === 'true',

  /**
   * Enable SMS notifications in Communications Center
   * 
   * When enabled: Shows "SMS / Text" as a delivery channel option in the message composer.
   * When disabled: SMS option is hidden to save costs (Twilio fees).
   * 
   * Default: false (disabled to save costs)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_SMS_NOTIFICATIONS=true
   */
  ENABLE_SMS_NOTIFICATIONS: process.env.NEXT_PUBLIC_ENABLE_SMS_NOTIFICATIONS === 'true',

  /**
   * Enable Help & Support link in the user dropdown
   * 
   * When enabled: Shows the "Help & Support" link in the header dropdown.
   * When disabled: The link is hidden (e.g., when replaced by a chatbot).
   * 
   * Default: false
   * Enable if needed: Set NEXT_PUBLIC_ENABLE_HELP_AND_SUPPORT=true
   */
  ENABLE_HELP_AND_SUPPORT: process.env.NEXT_PUBLIC_ENABLE_HELP_AND_SUPPORT === 'true',

  /**
   * Enable Integrations link in the user dropdown
   * 
   * When enabled: Shows the "Integrations" link in the header dropdown.
   * When disabled: The link is hidden.
   * 
   * Default: false
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_INTEGRATIONS=true
   */
  ENABLE_INTEGRATIONS: process.env.NEXT_PUBLIC_ENABLE_INTEGRATIONS === 'true',

  /**
   * Enable "Direct to Coach" payment model for private lessons
   *
   * When enabled: Shows "Direct to Coach" radio option in the private lesson form,
   * allowing parents to pay coaches directly (Venmo, Zelle, cash, etc.).
   *
   * When disabled: Only "Direct to Gym" is available for new programs.
   * Existing "Direct to Coach" programs continue to work normally.
   *
   * Default: false (hidden — paused based on gym owner feedback)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_DIRECT_TO_COACH_PAYMENT=true
   */
  ENABLE_DIRECT_TO_COACH_PAYMENT: process.env.NEXT_PUBLIC_ENABLE_DIRECT_TO_COACH_PAYMENT === 'true',

  /**
   * Enable AI Team Builder for tryout team formation
   *
   * When enabled: Shows "AI Team Builder" button in Tryout Workflow Step 2,
   * and allows access to the AI-powered team building page/modal.
   *
   * When disabled: Only the Manual Builder is shown. The AI Team Builder
   * page redirects to the manual builder. API routes remain functional
   * for development purposes.
   *
   * Default: false (disabled — feature not ready for production)
   * Enable for development: Set NEXT_PUBLIC_ENABLE_AI_TEAM_BUILDER=true
   */
  ENABLE_AI_TEAM_BUILDER: process.env.NEXT_PUBLIC_ENABLE_AI_TEAM_BUILDER === 'true',

  /**
   * Enable Travel Expenses feature for parents and independent athletes
   *
   * When enabled: Shows "Travel Expenses" link in the sidebar for parents
   * and independent athletes, with the expense tracking dashboard.
   *
   * When disabled: The Travel Expenses page is hidden from navigation.
   * Itineraries and other travel features are unaffected.
   *
   * Default: false (disabled — feature not yet implemented)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_TRAVEL_EXPENSES=true
   */
  ENABLE_TRAVEL_EXPENSES: process.env.NEXT_PUBLIC_ENABLE_TRAVEL_EXPENSES === 'true',

  /**
   * Enable Team Placement page for tryouts
   *
   * When enabled: Shows "Assign Team" button on evaluated athletes in the
   * tryout workflow, linking to the team placement page where staff can
   * offer a specific team to each athlete.
   *
   * When disabled: The "Assign Team" button is hidden. Staff can still
   * evaluate athletes and use the Team Builder, but cannot manually
   * assign individual placements yet.
   *
   * Default: false (disabled — placement workflow under development)
   * Enable when ready: Set NEXT_PUBLIC_ENABLE_TRYOUT_TEAM_PLACEMENT=true
   */
  ENABLE_TRYOUT_TEAM_PLACEMENT: process.env.NEXT_PUBLIC_ENABLE_TRYOUT_TEAM_PLACEMENT === 'true',

  /**
   * Enable Family / Guardian Access (U1 foundation flag)
   *
   * When enabled: A household ("family account") can have more than one adult
   * guardian, each with their own scoped permissions (who can see billing, make
   * payments, manage enrollments, sign policies, receive communications, etc.).
   * Gym owners/admins manage families; a primary guardian can invite a second
   * guardian.
   *
   * When disabled (default): None of the family/guardian screens, navigation,
   * invitations, or family-aware billing behavior appear. The underlying family
   * tables and RLS exist but are inert — no app code reads them, and the
   * additive billing RLS grants nothing because no active guardian rows exist.
   *
   * Default: false (disabled — feature under active development)
   * Enable for testing: Set NEXT_PUBLIC_ENABLE_FAMILY_GUARDIAN_ACCESS=true
   */
  ENABLE_FAMILY_GUARDIAN_ACCESS: process.env.NEXT_PUBLIC_ENABLE_FAMILY_GUARDIAN_ACCESS === 'true',

  /**
   * Enable Recurring Coupon Discounts (Phase B+ "ship dark" gate)
   *
   * When enabled: Staff see the "Keep applying this discount to every recurring
   * bill" toggle on the coupon form and the "max combined discount %" cap setting;
   * a redeemed recurring % code keeps discounting ongoing monthly class tuition and
   * standing private-lesson charges for the life of the enrollment/series.
   *
   * When disabled (default): None of the recurring-coupon staff controls appear and
   * no recurring discount is ever applied. The underlying columns, RPC change, and
   * resolver engine exist but are inert (mirrors the ENABLE_FAMILY_GUARDIAN_ACCESS
   * multi-phase rollout). This keeps the feature hidden until every phase (model →
   * money paths → display → reporting) is built and sandbox-verified end-to-end, so
   * a gym never creates a "recurring" coupon that does not yet recur.
   *
   * Default: false (disabled — feature under active phased development)
   * Enable when the full feature ships: Set NEXT_PUBLIC_ENABLE_RECURRING_COUPON_DISCOUNTS=true
   */
  ENABLE_RECURRING_COUPON_DISCOUNTS: process.env.NEXT_PUBLIC_ENABLE_RECURRING_COUPON_DISCOUNTS === 'true',

  /**
   * Athlete Standing Discounts (#660 — Support #77, Fresno Elite Cheer).
   *
   * When enabled: Staff can save a standing (recurring) percentage discount on an
   * individual athlete — with a separate % per billing stream (team monthly, class
   * tuition, private lessons, open gym) — from the Billing → Athlete Discounts page;
   * CheerVue applies it automatically to that athlete's future automatic bills,
   * stacking on existing discounts and respecting the gym's "max combined discount %"
   * cap. The athlete's view-only summary shows it on their profile.
   *
   * When disabled (default): None of the athlete-discount staff controls appear and
   * no athlete standing discount is ever resolved or applied. The table, the engine
   * widening, and the table-reader exist but stay inert (the engine never receives an
   * athlete percent unless a caller resolves one under this flag — mirrors the
   * Recurring Coupon Discounts phased rollout). This keeps the coupon-only billing
   * path provably byte-identical until every phase (model → money paths → display →
   * reporting) ships and is sandbox-verified per stream.
   *
   * Default: false (disabled — feature under active phased development)
   * Enable when the full feature ships: Set NEXT_PUBLIC_ENABLE_ATHLETE_STANDING_DISCOUNTS=true
   */
  ENABLE_ATHLETE_STANDING_DISCOUNTS: process.env.NEXT_PUBLIC_ENABLE_ATHLETE_STANDING_DISCOUNTS === 'true',

  /**
   * Dollar-amount athlete standing discounts (#2642 — ticket #15381, Sparks
   * Elite Athletics).
   *
   * When enabled: the Add/Edit athlete discount modal offers a $-amount type
   * alongside the percentage (a dollar amount off each bill, per billing
   * stream), plus the "Don't combine with coupon codes" toggle; the write core
   * accepts discount_type='fixed', per-stream amounts, and the toggle; the
   * assistant can set them too.
   *
   * When disabled (default): the write core rejects fixed/amount/toggle input
   * with a plain 400 and percent behavior stays byte-identical; no UI or
   * assistant mention appears. The columns and reader widening exist but stay
   * inert — no stored row can carry fixed values until a write is accepted, so
   * the engine paths never see one (KTD-16: flag checks concentrate at the
   * write/UI/assistant edges). Mirrors the ENABLE_ATHLETE_STANDING_DISCOUNTS
   * phased ship-dark rollout.
   *
   * NEXT_PUBLIC_ so the modal (client) and the write core/assistant (server)
   * read one value — same posture as ENABLE_ATHLETE_STANDING_DISCOUNTS.
   * Default: false (flip via a separate tiny PR after the demo-gym check).
   * Enable: Set NEXT_PUBLIC_ENABLE_ATHLETE_DISCOUNT_FIXED_AMOUNTS=true
   */
  ENABLE_ATHLETE_DISCOUNT_FIXED_AMOUNTS:
    process.env.NEXT_PUBLIC_ENABLE_ATHLETE_DISCOUNT_FIXED_AMOUNTS === 'true',

  /**
   * Team athlete class discount, Step 1: drop-ins (#5131, plan
   * docs/plans/2026-09-21-1720-feat-team-athlete-class-discount-plan.md, KTD10).
   *
   * When enabled: the Athlete Discount form shows the "Class drop-ins" box and
   * the write core and assistant accept it. Later PRs add the class's "Team
   * athlete discount" box and drop-in pricing under this same flag.
   *
   * When disabled (default): no "Class drop-ins" box, the write core rejects
   * its values with a plain 400, the assistant does not offer it, and no
   * drop-in price changes. The "Open Gym" rename and the "Monthly class fees."
   * hint are NOT behind this flag.
   *
   * NEXT_PUBLIC_ so the form (client) and the write core/assistant (server)
   * read one value. Geng flips it in Vercel after Step 1's last PR is live.
   * Enable: Set NEXT_PUBLIC_ENABLE_TEAM_CLASS_DROP_IN_DISCOUNTS=true
   */
  ENABLE_TEAM_CLASS_DROP_IN_DISCOUNTS:
    process.env.NEXT_PUBLIC_ENABLE_TEAM_CLASS_DROP_IN_DISCOUNTS === 'true',

  /**
   * Team athlete class discount, Step 2: monthly tuition (#5131, KTD10).
   *
   * Nothing reads this yet. Later PRs gate month-1 checkout, staff monthly
   * enrollment, and monthly bills on it. Default: false.
   * Enable: Set NEXT_PUBLIC_ENABLE_TEAM_CLASS_MONTHLY_DISCOUNTS=true
   */
  ENABLE_TEAM_CLASS_MONTHLY_DISCOUNTS:
    process.env.NEXT_PUBLIC_ENABLE_TEAM_CLASS_MONTHLY_DISCOUNTS === 'true',

  /**
   * Athlete discount groups (plan
   * docs/plans/2026-10-19-1004-feat-athlete-discount-groups-plan.md, KTD9).
   *
   * When enabled: staff can create discount groups (one named standing
   * discount many athletes share). The group screens, routes and Assistant
   * group tools are also open at any gym that already has a group that is not
   * deleted, so turning this off never strands a group staff can no longer
   * change (see isAthleteDiscountGroupsAvailable). The no-override guard and
   * the profile card's group line act on group rows regardless of this flag.
   *
   * NEXT_PUBLIC_ so the screens (client) and the routes (server) read one
   * value. Default: false. Geng turns it on after PR 4 (Assistant group tools).
   * Enable: Set NEXT_PUBLIC_ENABLE_ATHLETE_DISCOUNT_GROUPS=true
   */
  ENABLE_ATHLETE_DISCOUNT_GROUPS:
    process.env.NEXT_PUBLIC_ENABLE_ATHLETE_DISCOUNT_GROUPS === 'true',

  /**
   * Merch Store (#2637) — public per-gym storefront on the POS catalog.
   *
   * Ships in four PRs. The staff side (pictures, sizes, "Show in Merch Store",
   * the settings screen with the share link) lands before the public store
   * page does, so while OFF every store surface is hidden and its write routes
   * answer 404 — otherwise a gym could switch the store on and share a link
   * that 404s. Counter sales of sized items work regardless of this flag.
   *
   * NEXT_PUBLIC_ so the item form (client) and the settings route (server)
   * read one value. Default: false. Flip after the public store page (PR 2)
   * and the Orders screen (PR 3) merge: set NEXT_PUBLIC_ENABLE_MERCH_STORE=true.
   */
  ENABLE_MERCH_STORE: process.env.NEXT_PUBLIC_ENABLE_MERCH_STORE === 'true',

  /**
   * Event tickets (plan docs/plans/2026-10-19-2359-feat-event-tickets-plan.md,
   * KTD21). One launch flag hides every ticket surface: the Sell tickets card
   * on the event form, the public Events tab and event pages, checkout, the
   * check-in pages, and the app entry points. PR 1 adds only the tables and
   * the shared rules, which nothing reaches yet; PR 2 to PR 6 test with it on
   * locally; the launch turned it on in Vercel Production (2026-10-26).
   *
   * NEXT_PUBLIC_ so client screens and server routes read one value, at
   * build time. Default: false. NEXT_PUBLIC_ENABLE_EVENT_TICKETS=true is set
   * for Vercel Production only (previews use the production database).
   */
  ENABLE_EVENT_TICKETS: process.env.NEXT_PUBLIC_ENABLE_EVENT_TICKETS === 'true',

  /**
   * Mobile offline mode is on in the app (offline plan 2026-10-21, KTD13).
   *
   * The assistant's `mobile_offline` entry shows only while this is on, so it
   * never describes offline screens the app does not have yet. Turn it on in
   * the same PR that turns on the app's ENABLE_OFFLINE_MODE default
   * (cheervue-mobile-app/utils/config/featureFlags.ts); a guardrail test
   * (featureGuide.mobileOffline.test.ts) fails when the two differ.
   *
   * Default: on. Turn off: NEXT_PUBLIC_ENABLE_MOBILE_OFFLINE_MODE=false.
   */
  ENABLE_MOBILE_OFFLINE_MODE: process.env.NEXT_PUBLIC_ENABLE_MOBILE_OFFLINE_MODE !== 'false',

  /**
   * Payments-down owner alert (#1151).
   *
   * When enabled: CheerVue warns a gym owner/admin — in-app banner, plus push at
   * the moment payments break and a daily backstop — when their gym can't accept
   * online payments (payout setup not finished, or a previously-working account
   * was turned off). When disabled: no banner, and the payment-readiness API
   * returns { show: false } and the disable notification/backstop do nothing.
   *
   * SERVER-ONLY (intentionally NOT NEXT_PUBLIC_): a safety alert must not depend
   * on what shipped in the client bundle. Readiness is computed server-side
   * (resolver / API route / webhook / cron); the client banner only renders what
   * the authed API returns.
   *
   * Default: false (off in prod until verified, then turn on).
   * Enable: Set ENABLE_PAYMENTS_DOWN_ALERT=true
   */
  ENABLE_PAYMENTS_DOWN_ALERT: process.env.ENABLE_PAYMENTS_DOWN_ALERT === 'true',

  /**
   * Per-template invoice timing for payment plan templates (Triple C request).
   *
   * When enabled: a payment plan template can override the gym-wide Auto Monthly
   * Invoicing schedule with its own send day and/or due-date rule (the modal shows
   * an "Invoice timing" section, and the monthly invoice cron honors the override —
   * including catching up the current month when the chosen day has already passed).
   *
   * When disabled (default): the "Invoice timing" controls are hidden, the template
   * API ignores any override fields, and the cron is byte-identical to today (every
   * payment-plan invoice rides the gym-wide send day + due date). The override
   * columns exist but stay inert — mirrors the ship-dark rollout of
   * ENABLE_PAYMENTS_DOWN_ALERT / ENABLE_REQUIRE_MONTHLY_CLASS_AUTOPAY.
   *
   * NEXT_PUBLIC_ so the modal (client) and the cron/API (server) read one value.
   * Default: false (off in prod until the migration is applied + sandbox-verified).
   * Enable: Set NEXT_PUBLIC_ENABLE_PP_TEMPLATE_TIMING=true
   */
  ENABLE_PP_TEMPLATE_TIMING: process.env.NEXT_PUBLIC_ENABLE_PP_TEMPLATE_TIMING === 'true',

  /**
   * "Bill one month ahead" switch on the Automatic monthly invoicing card (#5694,
   * plan 2026-10-05-1209).
   *
   * Gates ONLY the settings control and the save path. Every reader of
   * `organizations.auto_invoice_bill_month_ahead` (the monthly run, forecasts,
   * status, assistant) honors a stored true whatever this flag says, so a gym
   * switched on stays consistent if the flag is turned back off.
   *
   * Default: false. Turn on only after all three PRs of the plan ship (class
   * signup covering two months, and the quit-early bill choice) AND the
   * migration is applied in production.
   * Enable: Set NEXT_PUBLIC_ENABLE_BILL_MONTH_AHEAD=true
   */
  ENABLE_BILL_MONTH_AHEAD: process.env.NEXT_PUBLIC_ENABLE_BILL_MONTH_AHEAD === 'true',

  /**
   * Auto-invoicing run recording (transparency work, docs/plans/2026-07-11-004).
   *
   * When enabled: the nightly generate-monthly-invoices cron upserts one
   * auto_invoice_runs row per due org (counts, per-team skip reasons, run window).
   * Recording only — no email, no user-visible change — so run history can
   * accumulate dark before any owner-facing surface flips on.
   *
   * When disabled (default): the cron is byte-identical to today; the table stays empty.
   *
   * Server-only (cron route reads it), so no NEXT_PUBLIC_ prefix.
   * Enable: Set ENABLE_AUTO_INVOICE_RUN_RECORDING=true
   */
  ENABLE_AUTO_INVOICE_RUN_RECORDING: process.env.ENABLE_AUTO_INVOICE_RUN_RECORDING === 'true',

  /**
   * Auto-invoicing transparency surfaces (docs/plans/2026-07-11-004).
   *
   * When enabled: owner summary email on scheduled sends, the enriched
   * Billing/Team Tuition status banner (upcoming send, skip preview, "Sent N"),
   * GET /api/admin/billing/auto-invoicing/status, and the assistant's
   * get_auto_invoice_status tool.
   *
   * When disabled (default): none of those surfaces exist; the base banner and
   * cron email behavior render byte-identical to today.
   *
   * Flip only after ENABLE_AUTO_INVOICE_RUN_RECORDING has produced >=2 nights of
   * run rows; this flag is also the one-flag rollback lever (recording stays on).
   * NEXT_PUBLIC_ so the banner (client) and email/route/tool (server) read one value.
   * Enable: Set NEXT_PUBLIC_ENABLE_AUTO_INVOICE_TRANSPARENCY=true
   */
  ENABLE_AUTO_INVOICE_TRANSPARENCY: process.env.NEXT_PUBLIC_ENABLE_AUTO_INVOICE_TRANSPARENCY === 'true',

  /**
   * Per-family exemption from Required Team Auto-Pay (#1310).
   *
   * When enabled: gym owners/admins can waive Required Team Auto-Pay for specific
   * families (per team+athlete) from the team's "Exceptions" list — the family is no
   * longer blocked at checkout, may manage their own auto-pay, and (by default) is
   * excused from the non-autopay surcharge — while the requirement stays ON for
   * everyone else on the team. The enforcement resolver reads the
   * team_autopay_exemptions table only while this flag is on.
   *
   * When disabled (default): the resolver returns "not exempt" without ever reading
   * the table, so every enforcement point (checkout gate, management lock, surcharge)
   * is byte-identical to today, and the team "Exceptions" entry point is hidden. This
   * keeps the merge-before-migrate deploy window safe — the flag is only flipped on
   * AFTER the migration is verified applied in prod.
   *
   * NEXT_PUBLIC_ so the team UI (client) and the resolver/API/cron (server) read one
   * value. Mirrors the ship-dark rollout of ENABLE_PP_TEMPLATE_TIMING.
   * Default: false (off in prod until the migration is applied + verified).
   * Enable: Set NEXT_PUBLIC_ENABLE_TEAM_AUTOPAY_EXEMPTIONS=true
   */
  ENABLE_TEAM_AUTOPAY_EXEMPTIONS:
    process.env.NEXT_PUBLIC_ENABLE_TEAM_AUTOPAY_EXEMPTIONS === 'true',

  /**
   * Parent bulk invoice payment (#1532): multi-select unpaid invoices on the
   * parent Billing page and pay them in ONE combined card charge.
   *
   * OFF (default): the Billing page renders byte-identical to today (no
   * checkboxes, no selection bar), POST /api/billing/payments/bulk returns 404,
   * and the assistant's paying-invoices knowledge describes the one-at-a-time
   * flow only. ON: multi-select UI + bulk endpoint + bulk-first assistant answer.
   *
   * NEXT_PUBLIC_ so the Billing UI (client), the bulk route (server), and the
   * assistant knowledge module read one value. Mirrors the ship-dark rollout of
   * ENABLE_PP_TEMPLATE_TIMING. Default: false (flip only after the Stripe
   * sandbox gate passes per docs/plans/2026-07-05-002-feat-bulk-invoice-payment-plan.md).
   * Enable: Set NEXT_PUBLIC_ENABLE_BULK_INVOICE_PAYMENT=true
   */
  ENABLE_BULK_INVOICE_PAYMENT:
    process.env.NEXT_PUBLIC_ENABLE_BULK_INVOICE_PAYMENT === 'true',

  /**
   * Wallet invoice payments (#—, Apple Pay / Google Pay): pay a persisted athlete
   * invoice with a device wallet from the web Pay Invoice modal, a no-login
   * tokenized public pay page, and the mobile app (which opens that page in the
   * system browser sheet).
   *
   * OFF (default): every surface is byte-identical to today — no wallet buttons
   * render anywhere, the public pay page and its create-intent/confirm routes 404
   * (a dark-launched public route is still a live route, so the server checks the
   * flag too, not just the client bundle), the invoice_payment_links table stays
   * inert (no code reads or writes it), and the assistant describes the existing
   * saved-card/bank invoice payment flow only. ON: wallet buttons + tokenized pay
   * page + create-intent/confirm/settle routes + wallet-aware assistant answer.
   *
   * NEXT_PUBLIC_ so the modal/page (client) AND the public + confirm routes
   * (server) read one value. Mirrors the ship-dark rollout of
   * ENABLE_TEAM_AUTOPAY_EXEMPTIONS / ENABLE_PP_TEMPLATE_TIMING: merge dark, flip
   * ONLY after the 20260710_007_invoice_payment_links migration is verified applied
   * in prod and a completed Stripe sandbox wallet charge passes the runtime gate.
   * Default: false.
   * Enable: Set NEXT_PUBLIC_ENABLE_WALLET_INVOICE_PAYMENTS=true
   */
  ENABLE_WALLET_INVOICE_PAYMENTS:
    process.env.NEXT_PUBLIC_ENABLE_WALLET_INVOICE_PAYMENTS === 'true',

  /**
   * Staff walk-in Open Gym punch redemption (dashboard surface).
   *
   * When enabled: front-desk staff (owner/admin/front_desk) can redeem a family's
   * prepaid Open Gym punch for a walked-in athlete from the dashboard — the
   * /api/open-gym/walk-in-redemption route goes live (two-phase preview → confirm
   * over the shared redeemOpenGymPunchWalkIn core; the ONE money path is still
   * bookOpenGymWithPunch).
   *
   * When disabled (default): the route dark-launches as 404 (a dark-launched
   * route is still a live route, so the server checks the flag — bulk-pay
   * precedent) and no staff UI entry point renders.
   *
   * NEXT_PUBLIC_ so the staff UI (client) and the route (server) read one value.
   * Default: false (off until sandbox-verified end-to-end).
   * Enable: Set NEXT_PUBLIC_ENABLE_OPEN_GYM_WALKIN_REDEMPTION=true
   */
  ENABLE_OPEN_GYM_WALKIN_REDEMPTION:
    process.env.NEXT_PUBLIC_ENABLE_OPEN_GYM_WALKIN_REDEMPTION === 'true',

  /**
   * A punch pass can cover regular class drop-ins as well as Open Gym (#6721).
   *
   * When disabled (default): every pass covers Open Gym only, as before. The pass
   * form shows no coverage choices and no code reads the coverage columns.
   * Stays off until staff can book a class drop-in with a pass and every place that
   * shows or sells passes knows what each covers, so a gym never ticks a class a pass
   * cannot be used for yet (list: docs/agent-context/billing.md, "What a pass covers").
   *
   * Enable: Set NEXT_PUBLIC_ENABLE_PUNCH_PASS_CLASS_COVERAGE=true
   */
  ENABLE_PUNCH_PASS_CLASS_COVERAGE:
    process.env.NEXT_PUBLIC_ENABLE_PUNCH_PASS_CLASS_COVERAGE === 'true',

  /**
   * Staff Bulk Collection "Collect outstanding balances" (#2041): staff-triggered
   * immediate collection of due/overdue invoices — autopay-authorized families are
   * charged through the SAME charge core as the daily cron, everyone else gets one
   * pay-now reminder email, with an honest preview/confirm and per-family results.
   *
   * OFF (default): the Billing page renders byte-identical to today (no Collect
   * button, no invoice selection), POST /api/admin/billing/collect/preview and
   * /execute return 404 (a dark-launched route is still a live route, so the
   * server checks the flag — bulk-pay precedent), and the assistant keeps the
   * honest "no bulk charge exists" answer. ON: Collect button + preview/execute
   * routes + collectOutstandingAction + flag-on assistant copy.
   *
   * NEXT_PUBLIC_ so the staff UI (client), both routes (server), and the
   * assistant knowledge/action modules read one value. Mirrors the ship-dark
   * rollout of ENABLE_BULK_INVOICE_PAYMENT. Flip order: apply the
   * admin_action_log action-type migration to prod → verify the constraint →
   * set NEXT_PUBLIC_ENABLE_STAFF_BULK_COLLECT=true in Vercel → redeploy.
   * Rollback = unset the flag. Default: false (flip only after the Stripe
   * sandbox double-charge probe passes per
   * docs/plans/2026-07-18-001-feat-staff-bulk-collect-plan.md).
   * Enable: Set NEXT_PUBLIC_ENABLE_STAFF_BULK_COLLECT=true
   */
  ENABLE_STAFF_BULK_COLLECT:
    process.env.NEXT_PUBLIC_ENABLE_STAFF_BULK_COLLECT === 'true',

  /**
   * Kiosk surface for walk-in Open Gym punch redemption (later PR).
   *
   * Defined now so the kiosk PR ships against an existing flag; NOTHING reads it
   * yet. When enabled (future): the athlete-facing kiosk gains a walk-in punch
   * redemption flow over the same shared core. When disabled (default): the kiosk
   * surface stays hidden/inert.
   *
   * Default: false (feature not built yet).
   * Enable when the kiosk PR ships: Set NEXT_PUBLIC_ENABLE_OPEN_GYM_WALKIN_KIOSK=true
   */
  ENABLE_OPEN_GYM_WALKIN_KIOSK:
    process.env.NEXT_PUBLIC_ENABLE_OPEN_GYM_WALKIN_KIOSK === 'true',

  /**
   * In-app feedback rating prompt: a dismissible chip on the gym dashboard that
   * invites an owner/admin to rate CheerVue 1-5 through the assistant, shown only
   * at a success moment (the gym is actively collecting real money) and heavily
   * fatigue-limited.
   *
   * When enabled: GET /api/feedback-prompt/eligibility can return eligible:true,
   * the chip renders, the event/review-redirect routes work, and the assistant's
   * record_feedback_rating tool can store a score. When disabled (default): the
   * eligibility route always returns { eligible: false }, no chip ever renders,
   * and the user_feedback_ratings table stays empty.
   *
   * SERVER-ONLY (intentionally NOT NEXT_PUBLIC_): who gets asked is decided
   * server-side — tenure, role, demo/platform-admin exclusions, fatigue history
   * and collected-money milestones are all things the client must not be able to
   * see or influence. The client renders only what the authenticated API returns,
   * so the flag never needs to reach the browser bundle. Same posture as
   * ENABLE_PAYMENTS_DOWN_ALERT.
   *
   * PAIRED WITH A SECOND SERVER-ONLY ENV VAR: FEEDBACK_PROMPT_ORG_ALLOWLIST, a
   * comma-separated list of organization ids (or `*` for every gym). BOTH are
   * required before a single owner sees the chip — an unset/empty allowlist
   * admits NOBODY, so an env var nobody remembered to set can never be read as
   * "ask every gym in production". Start with one or two friendly gyms, then
   * widen to `*`. It is read and parsed in
   * src/utils/feedback/feedbackPromptEligibility.ts (isOrgAllowlisted) rather
   * than here, because it is a list to evaluate rather than a boolean to expose.
   * Example: FEEDBACK_PROMPT_ORG_ALLOWLIST=8f3c...,b21a...
   *
   * Default: false (off in prod until the 20260724_001_user_feedback_ratings
   * migration is verified applied).
   * Enable: Set ENABLE_FEEDBACK_RATING_PROMPT=true (plus the allowlist above)
   */
  ENABLE_FEEDBACK_RATING_PROMPT: process.env.ENABLE_FEEDBACK_RATING_PROMPT === 'true',

  /**
   * Self-serve Bulk Roster Import ("Import Your Roster").
   *
   * OFF (default, owner decision 2026-08-05): the wizard is hidden — the Gym
   * Settings row is dropped and, while ENABLE_SEND_ROSTER_OFFER (below) is on,
   * it is replaced by "send us your roster": the Invite-a-family callout and
   * the new-gym welcome modal point at the offer, the no-athletes reminder
   * email retargets, and the wizard page shows a "we'll bring your roster over
   * for you" panel. With both flags off those surfaces point at join codes
   * instead — the state production ran through mid-September 2026. The three WRITE routes
   * (preview / commit / resolve-row) return 404 so a bookmarked page can't
   * still create athletes and mint parent accounts.
   *
   * THIS IS A FRONT-DOOR SWITCH, NOT A FEATURE TEARDOWN. The claim/invite tail
   * stays live in BOTH states — send-invites, correct-email, reminder-targets,
   * the public refresh-claim-link, and the sign-in claim handling all ignore
   * this flag. 146 already-imported parents across three gyms have not claimed
   * their accounts yet (Supernatural 90, Sparks 34, Starlights 8); gating those
   * routes would strand every one of them outside their gym with no way in.
   *
   * WHY OFF: six of the first thirteen real batches were abandoned mid-flow, and
   * on 2026-08-04 a two-day-old gym (GIC Cheer/Halo Haus) had all 21 rows
   * rejected and hand-typed 20 athletes instead (#3030, ticket #15439). Fixes
   * are landing (#3037, #3049, #3051, #3059 all merged 2026-08-05; #3043 still
   * open) — this flag holds the door shut until that work is finished and
   * verified end-to-end, rather than reverting it.
   *
   * NEXT_PUBLIC_ so the sidebar/modals/page (client) and the write routes +
   * reminder cron (server) read ONE value — a client-only flag would leave the
   * routes live. Mirrors the ship-dark rollout of ENABLE_STAFF_BULK_COLLECT.
   * Re-enable: Set NEXT_PUBLIC_ENABLE_ROSTER_IMPORT=true in Vercel + redeploy.
   */
  ENABLE_ROSTER_IMPORT: process.env.NEXT_PUBLIC_ENABLE_ROSTER_IMPORT === 'true',

  /**
   * "Want us to set it up for you?" — the concierge offer that replaced the self-serve
   * wizard while ENABLE_ROSTER_IMPORT is off. Owner decision 2026-09-17: bring it back
   * ON, now that the send screen states the four things a file needs, hands over a
   * template, and CHECKS the file in the browser before it can be sent. It was switched
   * off on 2026-09-10 because the files gyms sent could not be loaded without days of
   * back-and-forth (#4447, #4674) — the check is the answer to that, so the offer ships
   * with it rather than staying dark.
   *
   * OFF hides the offer on every surface that renders SendRosterOffer (setup guide path
   * selector + checklist, the Athletes empty state, the /dashboard/admin/import-roster
   * bookmark page) and switches the copy that used to point at it (welcome modal, the
   * Invite-a-family callout, the two "get your athletes in" reminder emails, the
   * assistant's KB entries + system prompt) to join codes and Add Athlete. Only
   * matters while ENABLE_ROSTER_IMPORT is off; the wizard wins when both are on.
   *
   * NEXT_PUBLIC_ for the same reason as ENABLE_ROSTER_IMPORT: client surfaces and the
   * server-rendered email/prompt read ONE value.
   *
   * Default: TRUE — the deploy itself carries the flip, and the env var is now a KILL
   * SWITCH: NEXT_PUBLIC_ENABLE_SEND_ROSTER_OFFER=false hides every surface again without
   * a code change (same shape as ENABLE_INDIVIDUAL_SCOPE).
   */
  ENABLE_SEND_ROSTER_OFFER: process.env.NEXT_PUBLIC_ENABLE_SEND_ROSTER_OFFER !== 'false',

  /**
   * Refuse a parent adding a child who is ALREADY at that gym under another family,
   * instead of creating a second record and flagging it for staff (#436's
   * allow-and-flag). Off until the gym has somewhere to answer from.
   *
   * NOT NEXT_PUBLIC_: enforcement is entirely server-side (the add-athlete route),
   * and a NEXT_PUBLIC_ value is baked into the browser bundle at build time, so
   * flipping it would need a redeploy rather than an env change. The parent-facing
   * screen reacts to the route's 409, not to a flag it reads itself.
   *
   * DO NOT ENABLE until #3105 ships. The owner declined a per-parent escape hatch,
   * so a gym that ignores its follow-up queue can otherwise leave a paying family
   * unable to sign up, with nothing escalating. #3105 is that escalation.
   *
   * DO NOT ENABLE until PR3 ships either. Nothing at the gym reads
   * `kind='access_requested'` yet — the only non-test references are the writer and
   * the gate — so today "we've asked them" is a row in a table nobody looks at.
   *
   * AND confirm on the day of the flip that PR1's migration (`20260807_001`) is
   * actually live in production, not merely merged. Without it the request insert
   * fails 23514/PGRST204 rather than 23505, which takes the failure branch: every
   * blocked parent is refused with nothing filed, silently, for everyone.
   *
   * Also check before enabling: the 4 gyms that already hold duplicate pairs need
   * cleaning, or their existing ghost record will block the real parent. And note
   * that the per-guardian cap bounds the ROWS written, not the 409 — there is no
   * rate limit on this path, so the refusal is an unlimited confirmation oracle for
   * a parent of that gym who already knows a child's exact name and birthday.
   * The full pre-enable list lives in docs/plans/2026-08-07-duplicate-block-PR2-RESUME.md.
   */
  ENABLE_ORG_DUPLICATE_BLOCK: process.env.ENABLE_ORG_DUPLICATE_BLOCK === 'true',

  /**
   * The staff "Delete athlete" action on the athlete detail page — hard-deletes
   * the athlete from CheerVue entirely, not from this gym. Parked OFF on
   * 2026-08-06 (owner decision, #3080); re-shaped as a "delete a mistake athlete"
   * action and ready to flip back on (owner decision, 2026-09-02).
   *
   * (This flag used to also gate "Convert to self-managed". That half no longer
   * exists: the card, the convert-to-self-managed route, and the
   * convertAthleteToSelfManaged resolver were deleted outright on 2026-08-27 —
   * owner decision, account-connection repairs go through CheerVue support —
   * so flipping this flag on brings back ONLY the delete action.)
   *
   * THE RULE WHEN ON. An athlete record belongs to the family, not to one gym,
   * so the delete works only while the athlete is still the gym's own draft: no
   * parent or athlete login is connected to it (a CheerVue-minted claimable
   * account that never confirmed or signed in does not count), and no money has
   * moved. Inside that line the delete clears what the Add New Athlete wizard
   * created — team spots, class spots, location memberships, non-completed policy
   * requests, pending invitations — and voids unpaid invoices (kept, detached).
   * Outside it the route refuses and points to Archive or support. Rule and
   * refusal codes: src/app/api/athletes/[athleteId]/staff-delete/route.ts.
   *
   * NOT A TEARDOWN. Archive and the parent-facing no-history removal
   * (/api/athletes/[id]/delete) are UNTOUCHED by this flag. (Untouched, not
   * necessarily live: the parent removal carries its own
   * ENABLE_FAMILY_GUARDIAN_ACCESS gate. Archive is live unconditionally.)
   *
   * THE DATABASE HALF: the `athletes_delete_via_cache` RLS policy that once let a
   * staff user DELETE straight through PostgREST is dropped, and DELETE on
   * public.athletes is revoked from authenticated/anon, by migration
   * 20260827_001_revoke_client_athlete_delete.sql (#3082). Read that back in
   * production before flipping this flag; the route's guards are the only ones.
   *
   * NEXT_PUBLIC_ so the page (client) and the write route (server) read ONE
   * value; a client-only flag would hide the button and leave the route live.
   * Re-enable: NEXT_PUBLIC_ENABLE_STAFF_ATHLETE_RECORD_ACTIONS=true in Vercel +
   * redeploy.
   */
  ENABLE_STAFF_ATHLETE_RECORD_ACTIONS:
    process.env.NEXT_PUBLIC_ENABLE_STAFF_ATHLETE_RECORD_ACTIONS === 'true',

  /**
   * Card on file: a parent's per-card consent that the gym may charge that card
   * for an unpaid balance, and (PR 2) the staff "Charge card on file" button on
   * an unpaid invoice (#4451, docs/plans/2026-09-05-004-feat-charge-card-on-file-plan.md).
   *
   * OFF (default): the add-card modal and Payment Methods tab render as before
   * (no consent sentence, no switch), the consent PATCH route returns 404, the
   * assistant does not describe the consent, and get_my_autopay_status omits it.
   * The consent columns exist either way (migration 20261018_001) but stay at
   * their defaults. ON: consent UI + PATCH + assistant entry.
   *
   * Flip order: apply PR 1's migration → merge PR 2 → apply PR 2's migration and
   * read it back → set NEXT_PUBLIC_ENABLE_CARD_ON_FILE_CHARGE=true in Vercel →
   * redeploy. Nobody flips it after PR 1 alone to start collecting consent early.
   * Rollback = unset the flag.
   *
   * NEXT_PUBLIC_ so the modal (client), the routes (server), and the assistant
   * knowledge module read one value.
   * Enable: Set NEXT_PUBLIC_ENABLE_CARD_ON_FILE_CHARGE=true
   */
  ENABLE_CARD_ON_FILE_CHARGE:
    process.env.NEXT_PUBLIC_ENABLE_CARD_ON_FILE_CHARGE === 'true',

  // Enable only after tuition migrations and revision-aware invoice writers are verified.
  ENABLE_TEAM_TUITION_AMENDMENTS: process.env.ENABLE_TEAM_TUITION_AMENDMENTS === 'true',

  /**
   * Show the gym-facing "Search engines" switch on Gym Settings → Brand Profile.
   *
   * The switch writes `search_listing_enabled` into `organizations.settings`, and
   * nothing reads that key until the public pages become server-rendered. So while
   * this is off the setting is inert, and — more to the point — the section's copy
   * would not yet be true: it tells an owner that turning the switch off removes
   * published content from their pages, and there is no published content yet.
   *
   * ONE flag gates this section. It has its own card on Brand Profile, deliberately
   * outside the Public Profile card — that one is behind ENABLE_GYM_DISCOVERY, which
   * stays off until CheerVue reaches 100 gym customers (owner decision, 2026-09-09),
   * and the search switch must not wait on it. Setting this flag alone ships the card.
   *
   * So the remaining prerequisite is the real one: do not flip this until the public
   * catalog and class pages actually READ the key. Until they do, the card's off-state
   * tells an owner search engines are being told not to list pages that emit no signal
   * either way.
   *
   * Flip order: merge this → merge the public catalog/class page conversions that read
   * the key → send the launch notice and let its notice period elapse → set
   * NEXT_PUBLIC_ENABLE_SEARCH_LISTING=true in Vercel. Rollback = unset the flag; any
   * key already written stays and simply goes back to being unread.
   *
   * NEXT_PUBLIC_ so the settings screen (client) and the public pages (server)
   * read one value.
   * Enable: Set NEXT_PUBLIC_ENABLE_SEARCH_LISTING=true
   */
  ENABLE_SEARCH_LISTING: process.env.NEXT_PUBLIC_ENABLE_SEARCH_LISTING === 'true',

  /**
   * "First class date" on an Ongoing Class (#4724, plan 2026-09-10-001).
   *
   * When enabled: the Create/Edit Class form shows the optional date, and the
   * public catalog / detail page say "Starts <date>" for a class whose first
   * meeting is still ahead. When disabled: the field is hidden and the catalog
   * renders as before; the column and the schedule cascade still exist but no
   * class can acquire a date through the UI.
   *
   * Default: false. PR 1 ships the date and every meeting surface DARK: with
   * the flag off the class routes write NO date (whatever a client sends) and
   * the public catalog/detail emit none. The server refuses monthly/staff
   * enrollment before the date while the billing half (PR 2) is unshipped.
   * Flip in Vercel only AFTER PR 2 merges — before that, a dated class would
   * list in the catalog with sign-ups held, which is worse than what the gym
   * has today. Rollback (flag on → off) is safe: an edit keeps a class's
   * stored date and its schedule; it just cannot set a new one.
   * Enable: Set NEXT_PUBLIC_ENABLE_CLASS_FIRST_CLASS_DATE=true
   */
  ENABLE_CLASS_FIRST_CLASS_DATE: process.env.NEXT_PUBLIC_ENABLE_CLASS_FIRST_CLASS_DATE === 'true',

  /**
   * Birthday shout-outs (#2633): the Gym Settings → Birthday Shout-Outs page,
   * its rail row, its two routes, and the assistant's knowledge of them.
   * Default false: everything stays hidden while the feature is built in
   * steps (docs/plans/2026-10-16-0941-feat-athlete-birthday-shoutouts-plan.md).
   * Flip only in step 6 (U10), after the mobile release with the family
   * controls is live. Until then the save route also refuses to turn the
   * gym switch on, even with this flag on.
   * Enable: Set NEXT_PUBLIC_ENABLE_BIRTHDAY_SHOUTOUTS=true
   */
  ENABLE_BIRTHDAY_SHOUTOUTS: process.env.NEXT_PUBLIC_ENABLE_BIRTHDAY_SHOUTOUTS === 'true',

  /**
   * Custom staff roles (#6135, plan
   * docs/plans/2026-10-27-1252-feat-custom-staff-roles-plan.md KTD11).
   *
   * When enabled: the Owner can create custom roles and the Owner and
   * Administrators can give them out. The routes that do that arrive in later
   * PRs (U5 assigning, U7 the role editor) and read this flag.
   *
   * When disabled (default): nothing creates or assigns a custom role. The
   * permission checks (requireStaffAccess, useStaffAccess, the SQL
   * staff_has_permission) are live either way; with no custom role rows they
   * change nothing for anyone.
   *
   * NEXT_PUBLIC_ so the screens (client) and the routes (server) read one
   * value. Flip only in the launch PR (U9), after every other PR is merged and
   * read back in production.
   * Enable: Set NEXT_PUBLIC_ENABLE_CUSTOM_STAFF_ROLES=true
   */
  ENABLE_CUSTOM_STAFF_ROLES: process.env.NEXT_PUBLIC_ENABLE_CUSTOM_STAFF_ROLES === 'true',
  /**
   * First automatic send Ready check (#6636, plan
   * docs/plans/2026-11-01-1857-feat-first-send-ready-check-plan.md KTD10).
   *
   * When enabled: until a gym has said yes once
   * (organizations.auto_invoice_first_send_confirmed_at), the monthly run writes
   * that gym's bills as drafts marked metadata.first_send_held, sends nothing to
   * families, and the reminder cron emails the owner. The owner sends them from the
   * dashboard card.
   *
   * When disabled (default): the run and the reminder cron behave exactly as
   * before. Readers still honor marks and stamps already written (the card, the
   * payout path's filter, the confirm route), so turning it off never strands a
   * held bill or lets the payout button send one.
   *
   * SERVER-ONLY: the run, the cron and the routes decide; the card renders what
   * the authed route returns. Turn on only after the migration backfill is read
   * back in production, and never within 24 hours before a 1st.
   * Enable: Set ENABLE_FIRST_SEND_READY_CHECK=true
   */
  ENABLE_FIRST_SEND_READY_CHECK: process.env.ENABLE_FIRST_SEND_READY_CHECK === 'true',
} as const;

export type FeatureFlags = typeof FEATURE_FLAGS;
